import React from "react";
import { Box, Typography, Button } from "@mui/material";

/**
 * Renders a promotional ad banner that is tied to the active sort selection.
 * Supports an ad for the "cheapest" and "quickest" sorts (same creative).
 * Can be extended to support additional ads for other sort types.
 */
function SortAdBanner({ activeSort = "best" }) {
  const renderCheapestAd = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#fff",
        borderRadius: 2.5,
        boxShadow: "0 1px 2px rgba(25,32,36,0.12)",
        border: "1px solid #E6EBEF",
        px: 3,
        py: 2,
        mb: 2,
        transition: "background-color .2s ease",
      }}
      role="region"
      aria-label="Sponsored deal for Cheapest sort"
    >
      {/* Left logo area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 150,
        }}
      >
        <Box
          component="img"
          src="https://www.kayak.com/picasso/svg?url=/kimg/a/suppliers/5001447/vector-logos/f949c98dac604f1d.svg&padX=5&padY=5"
          alt="Alaska Airlines logo"
          sx={{ height: 56, width: "auto", cursor: "pointer" }}
        />
      </Box>

      {/* Middle content */}
      <Box
        className="ad-body"
        sx={{
          flex: 1,
          pr: 2,
          py: 1.25,
          borderRadius: 1.5,
          transition: "background-color .2s ease",
          cursor: "pointer",
          "&:hover": { backgroundColor: "#E6EBEF" },
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#1b1f23", fontSize: 14, mb: 0.75 }}>
          No change fees.
        </Typography>
        <Typography sx={{ color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
          Your peace of mind is always top of mind. Book now with no change fees on main & first class fares.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.25 }}>
          <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>$105</Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13 }}>nonstop</Typography>
        </Box>

        <Typography sx={{ color: "#6b7280", fontSize: 12, mt: 1.5 }}>
          Alaska Airlines | Ad
        </Typography>
      </Box>

      {/* Right price + CTA */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          justifyContent: "center",
          px: 2.5,
          py: 2,
          minWidth: 100,
          border: "1px solid #E6EBEF",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(25,32,36,0.08)",
          "&:hover": { backgroundColor: "#E6EBEF" },
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 26, color: "#111827", lineHeight: 1 }}>
          $105
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1.25 }}>Alaska Airlines</Typography>
        <Button
          size="small"
          onClick={() => console.log("Ad CTA clicked: Alaska View Deal")}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            mb: 1,
            px: 3.5,
            background: "linear-gradient(90deg, #ff7a00 0%, #ff3d00 100%)",
            color: "#fff",
          }}
        >
          View Deal
        </Button>
      </Box>
    </Box>
  );

  const renderBestAd = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        backgroundColor: "#fff",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(25,32,36,0.12)",
        border: "1px solid #E6EBEF",
        px: 3,
        py: 2.5,
        mb: 2,
        gap: 2.5,
      }}
      role="region"
      aria-label="Sponsored deal for Best sort"
    >
      {/* Left logo */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            border: "1px solid #E6EBEF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box component="img" src="/united.svg" alt="United Airlines" sx={{ height: 40 }} />
        </Box>
      </Box>

      {/* Middle copy */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: "#1b1f23", fontSize: 16 }}>
          Book your next adventure with United Airlines.
        </Typography>
        <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 0.25 }}>
          No fees to change your flight, excluding Basic Economy fares. Terms apply.
        </Typography>

        {/* Two sample legs */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>10:53 pm – 12:25 am</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>nonstop</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>1h 32m</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>SFO-LAX</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>4:20 pm – 5:46 pm</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>nonstop</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>1h 26m</Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 13 }}>LAX-SFO</Typography>
          </Box>
        </Box>
      </Box>

      {/* Right stacked price cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 220 }}>
        <Box
          sx={{
            border: "1px solid #E6EBEF",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            boxShadow: "0 1px 2px rgba(25,32,36,0.08)",
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: 26, lineHeight: 1 }}>$142</Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 1 }}>Basic Economy • United Airlines</Typography>
          <Button
            size="small"
            onClick={() => console.log("Ad CTA clicked: United Basic View Deal")}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              px: 3,
              background: "linear-gradient(90deg, #ff7a00 0%, #ff3d00 100%)",
              color: "#fff",
            }}
          >
            View Deal
          </Button>
        </Box>
        <Box
          sx={{
            border: "1px solid #E6EBEF",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            boxShadow: "0 1px 2px rgba(25,32,36,0.08)",
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: 26, lineHeight: 1 }}>$212</Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 1 }}>Economy • United Airlines</Typography>
          <Button
            size="small"
            onClick={() => console.log("Ad CTA clicked: United Economy View Deal")}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              px: 3,
              background: "linear-gradient(90deg, #ff7a00 0%, #ff3d00 100%)",
              color: "#fff",
            }}
          >
            View Deal
          </Button>
        </Box>
      </Box>
    </Box>
  );

  if (activeSort === "cheapest" || activeSort === "quickest") return renderCheapestAd();
  if (activeSort === "best") return renderBestAd();
  return null;
}

export default SortAdBanner;


